import "@/i18n/config";
import { EventChangeArg, EventInput } from "@fullcalendar/core";
import interactionPlugin from "@fullcalendar/interaction";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import React, { useEffect, useState } from "react";
import { MdOutlineHelpOutline } from "react-icons/md";
import { useParams } from "react-router-dom";

import { InterviewBookingHelpModal } from "@/client_app/components/InterviewBookingHelpModal";
import {
  cancelBooking,
  createBooking,
  getInterviewAvailable,
} from "@/client_app/components/services/FetchAPIService";

interface Props {
  isAdmin?: boolean;
}
export function InterviewBookingPage(props: Props = { isAdmin: false }) {
  const { isAdmin } = props;
  const [events, setEvents] = useState<EventInput[]>([]);
  const [hasBooking, setHasBooking] = useState(false);
  const [data, setData] = useState();
  const [status, setStatus] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [openModal, setOpenModal] = useState(true);

  const [headerToolbar, setHeaderToolbar] = useState({
    left: "prev,next",
    center: "title",
    right: "timeGridWeek,timeGridDay",
  });

  const { id } = useParams<{ id: string }>();
  useEffect(() => {
    const updateToolbar = () => {
      if (window.innerWidth < 640) {
        // Small screen (e.g., mobile)
        setHeaderToolbar({
          left: "prev,next",
          center: "",
          right: "title",
        });
      } else {
        // Larger screens
        setHeaderToolbar({
          left: "prev,next today",
          center: "title",
          right: "timeGridWeek,timeGridDay",
        });
      }
    };

    updateToolbar(); // Initial check
    window.addEventListener("resize", updateToolbar);

    return () => window.removeEventListener("resize", updateToolbar);
  }, []);
  useEffect(() => {
    if (id) {
      getInterviewAvailable(id)
        .then((res) => {
          if (!res.ok) {
            if (res.status === 404) {
              throw new Error("Interview not found");
            } else {
              throw new Error("Failed to fetch events");
            }
          }
          return res.json();
        })
        .then((data) => {
          setData(data);
          setStatus(data["status"]);
          let events = data["events"].map((e) => ({
            start: e.start,
            end: e.end,
            display: "background",
            color: "green",
          }));
          if (data["booking"]) {
            setHasBooking(true);
            events = [
              ...events,
              {
                id: data["booking"].id,
                start: data["booking"].start,
                end: data["booking"].end,
                title: data["booking"].status,
                editable: data["booking"].status === "pending" || isAdmin,
              },
            ];
          }
          setEvents(events);
        })
        .catch((err) => {
          setError(err.message);
        });
    }
  }, [id]);

  if (error) {
    return <h1 className='text-headline2 mb-4'>Error: {error}</h1>;
  }
  const handleSelect = (info) => {
    if (hasBooking || !data) {
      return;
    }

    const startStr = info.startStr;
    const endStr = info.endStr;
    if (
      window.confirm(
        `Confirm this time slot from ${new Date(startStr).toLocaleString(
          "en-AU"
        )} to ${new Date(endStr).toLocaleString("en-AU")}?`
      )
    ) {
      createBooking(id, null, info.startStr, info.endStr, isAdmin)
        .then((res) => {
          if (!res.ok) {
            if (res.status === 404) {
              throw new Error("Interview not found");
            } else {
              throw new Error("Failed to fetch events");
            }
          }
          return res.json();
        })
        .then((data) => {
          setHasBooking(true);
          const event = data["event"];
          setEvents((prev) => [
            ...prev,
            {
              id: event.id,
              start: event.start,
              end: event.end,
              title: event.status,
              editable: event.status === "pending" || isAdmin,
            },
          ]);
        });
    }
  };
  const handleCancelBooking = () => {
    if (
      window.confirm(
        "Are you sure to cancel this interview? This action cannot be undone."
      )
    ) {
      cancelBooking(id).then();
      setStatus("cancelled");
    }
  };
  const handleEventChange = (changeInfo: EventChangeArg) => {
    if (!data) {
      return;
    }
    const { event, oldEvent } = changeInfo;

    const startStr = event.startStr;
    const endStr = event.endStr;
    if (
      window.confirm(
        `Confirm this time slot from ${new Date(startStr).toLocaleString(
          "en-AU"
        )} to ${new Date(endStr).toLocaleString("en-AU")}?`
      )
    ) {
      createBooking(id, event.id, startStr, endStr, isAdmin)
        .then((res) => {
          if (!res.ok) {
            if (res.status === 404) {
              throw new Error("Interview not found");
            } else {
              throw new Error("Failed to fetch events");
            }
          }
          return res.json();
        })
        .then((data) => {
          const event = data["event"];
          setEvents((prev) => [
            ...prev,
            {
              id: event.id,
              start: event.start,
              end: event.end,
              title: event.status,
              editable: event.status === "pending" || isAdmin,
            },
          ]);
        });
    } else {
      setEvents((prev) => [...prev, oldEvent]);
    }
  };

  if (!data) {
    return <h1 className='text-headline2 mb-4'>Loading...</h1>;
  }

  if (data["status"] === "cancelled" && !isAdmin) {
    return (
      <h1 className='text-headline2 mb-4 text-center'>
        Interview is cancelled
      </h1>
    );
  }
  return (
    <div className='text-legacyColor-neutral-1-500'>
      <header className='py-8 bg-primary-50 flex justify-between items-center'>
        <div>
          <h1
            className={`${
              isAdmin ? "text-headline2" : "text-3xl font-bold"
            } xs::text-xl mb-4 px-12`}
          >
            {isAdmin ? "Interview Section" : "Interview Booking"}
          </h1>
          <div className='text-lg px-12'>{data["job_role"]}</div>
        </div>
        {isAdmin && status != "cancelled" && (
          <button
            className='btn btn-sm btn-secondary mx-12'
            onClick={handleCancelBooking}
          >
            Cancel Interview
          </button>
        )}
      </header>
      <div className='px-12 py-8'>
        {!isAdmin && (
          <div className='pb-6'>
            <span className='font-bold'>Talent representative:</span>{" "}
            <span className='capitalize'>{data["talent"]["name"]}</span> -{" "}
            {data["talent"]["email"]}
          </div>
        )}
        <div className='flex justify-between pb-6'>
          <div>Candidate: {data["candidate_name"]}</div>
          {!isAdmin && (
            <>
              {openModal && (
                <InterviewBookingHelpModal
                  closeModal={() => setOpenModal(false)}
                />
              )}
              <button
                onClick={() => setOpenModal(true)}
                className='items-center flex justify-end'
              >
                <div className='pr-1 font-bold'>Help</div>{" "}
                <MdOutlineHelpOutline />{" "}
              </button>
            </>
          )}
          {isAdmin && (
            <>
              <div>Interviewer Email: {data["email"]}</div>
              <div>Format: {data["format"]}</div>
              <div>Status: {status}</div>
            </>
          )}
        </div>
        {data["booking"] && data["booking"]["link"] && (
          <div className='pb-6'>
            <a className='underline' href={data["booking"]["link"]}>
              Meeting link
            </a>
          </div>
        )}

        <FullCalendar
          plugins={[timeGridPlugin, interactionPlugin]}
          timeZone='Australia/Melbourne'
          initialView={isAdmin ? "timeGridWeek" : "timeGridDay"}
          allDaySlot={false}
          selectable={true}
          editable={true}
          events={events}
          eventChange={handleEventChange}
          select={handleSelect}
          slotDuration='00:30:00'
          height={window.innerHeight - 250}
          headerToolbar={headerToolbar}
          scrollTime='08:00:00'
        />
      </div>
    </div>
  );
}
